import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Lock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
    // Remove the token from the URL bar so it can't leak into browser
    // history / referrer headers on subsequent navigations.
    if (t) {
      window.history.replaceState({}, document.title, "/reset-password");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
        confirm_password: confirm,
      });
      setSuccess(true);
      setTimeout(() => nav("/auth"), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || "This reset link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg)" }}>
        <div className="w-full max-w-md pm-card p-8 text-center" data-testid="reset-missing-token">
          <h1 className="font-display text-2xl font-bold mb-2">Missing reset token</h1>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            The reset link is malformed. Please request a new one.
          </p>
          <Link to="/forgot-password" className="pm-btn-primary inline-flex">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md pm-card p-8" data-testid="reset-password-card">
        <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm mb-6" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        {success ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 mx-auto" style={{ background: "#10b98122" }}>
              <CheckCircle2 size={26} style={{ color: "#10b981" }} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Password reset</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              You can now sign in with your new password. Redirecting…
            </p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "var(--hl-blue)" }}>
              <Lock size={20} style={{ color: "var(--primary)" }} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Choose a new password</h1>
            <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              Enter and confirm your new password below. This reset link can only be used once.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-sm font-medium block mb-1.5">New password</label>
                <input
                  className="pm-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  data-testid="reset-password"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Confirm new password</label>
                <input
                  className="pm-input"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  data-testid="reset-confirm"
                />
              </div>
              {error && <div className="text-sm p-3 rounded-xl" style={{ background: "#fdecea", color: "var(--weakness)" }} data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={loading} className="pm-btn-primary w-full flex items-center justify-center gap-2" data-testid="reset-submit-btn">
                {loading && <Loader2 size={16} className="animate-spin" />}
                Reset password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
